import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavBar from '../../components/AdminNavBar';
import supabase from '../../utils/supabaseClient';
import imageCompression from 'browser-image-compression';
import './UploadBanners.css';

function UploadBanners() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [bannerType, setBannerType] = useState('desktop'); // 'desktop' or 'mobile'
  const [existingBanners, setExistingBanners] = useState({ desktop: [], mobile: [] });
  const [showExistingBanners, setShowExistingBanners] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    fetchBanners();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN') {
          setUser(session?.user || null);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuth = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('Error getting user:', error);
      } else {
        setUser(user);
      }
    } catch (error) {
      console.error('Error checking auth:', error);
    } finally {
      setAuthLoading(false);
    }
  };

  const fetchBanners = async () => {
    try {
      console.log('Fetching banners from database...');
      const { data, error } = await supabase
        .from('banners')
        .select('*')
        .order('id', { ascending: true });

      if (error) {
        console.error('Error fetching banners:', error);
        showToast('Error al cargar banners desde la base de datos', 'error');
      } else {
        console.log('Banners fetched successfully:', data);
        // Force re-render by creating a new array reference
        setBanners([...(data || [])]);
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      showToast('Error al cargar banners', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingBanners = async () => {
    setLoadingExisting(true);
    try {
      // Fetch desktop banners from storage
      const { data: desktopFiles, error: desktopError } = await supabase.storage
        .from('resources')
        .list('banner desktop', {
          limit: 100,
          offset: 0
        });

      // Fetch mobile banners from storage
      const { data: mobileFiles, error: mobileError } = await supabase.storage
        .from('resources')
        .list('banner mobile', {
          limit: 100,
          offset: 0
        });

      if (desktopError) {
        console.error('Error fetching desktop banners:', desktopError);
      }
      if (mobileError) {
        console.error('Error fetching mobile banners:', mobileError);
      }

      // Get public URLs for desktop banners
      const desktopBanners = (desktopFiles || [])
        .filter(file => file.name && !file.name.startsWith('.'))
        .map(file => {
          const { data: { publicUrl } } = supabase.storage
            .from('resources')
            .getPublicUrl(`banner desktop/${file.name}`);
          return {
            name: file.name,
            url: publicUrl,
            type: 'desktop'
          };
        });

      // Get public URLs for mobile banners
      const mobileBanners = (mobileFiles || [])
        .filter(file => file.name && !file.name.startsWith('.'))
        .map(file => {
          const { data: { publicUrl } } = supabase.storage
            .from('resources')
            .getPublicUrl(`banner mobile/${file.name}`);
          return {
            name: file.name,
            url: publicUrl,
            type: 'mobile'
          };
        });

      setExistingBanners({
        desktop: desktopBanners,
        mobile: mobileBanners
      });
    } catch (error) {
      console.error('Error fetching existing banners:', error);
    } finally {
      setLoadingExisting(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);

    // Create preview URLs
    const urls = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
  };

  const uploadBanners = async () => {
    if (selectedFiles.length === 0) return;
    
    // Check authentication
    if (!user) {
      showToast('Debes iniciar sesión para subir banners', 'error');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;
    
    try {
      for (const file of selectedFiles) {
        try {
          // Optimize image before upload
          const compressionOptions = {
            maxSizeMB: 1, // Maximum file size in MB
            maxWidthOrHeight: bannerType === 'desktop' ? 1920 : 800, // Desktop: 1920px, Mobile: 800px
            useWebWorker: true,
            fileType: 'image/webp', // Convert to WebP for better compression
            initialQuality: 0.85 // 85% quality - good balance
          };

          console.log(`Compressing ${file.name}...`);
          const compressedFile = await imageCompression(file, compressionOptions);
          
          // Calculate compression ratio
          const originalSize = (file.size / 1024 / 1024).toFixed(2);
          const compressedSize = (compressedFile.size / 1024 / 1024).toFixed(2);
          const savings = (((file.size - compressedFile.size) / file.size) * 100).toFixed(0);
          console.log(`Original: ${originalSize}MB → Compressed: ${compressedSize}MB (${savings}% smaller)`);

          // Upload optimized file to storage
          const fileName = `${file.name.replace(/\.[^/.]+$/, "")}_${Date.now()}.webp`;
          const folderPath = bannerType === 'desktop' ? 'banner desktop' : 'banner mobile';
          const filePath = `${folderPath}/${fileName}`;

          console.log(`Uploading optimized banner to: ${filePath}`);

          const { error: uploadError } = await supabase.storage
            .from('resources')
            .upload(filePath, compressedFile);

          if (uploadError) {
            console.error('Error uploading banner:', uploadError);
            errorCount++;
            continue;
          }

          // Get public URL and activate banner immediately
          const { data: { publicUrl } } = supabase.storage
            .from('resources')
            .getPublicUrl(filePath);

          // Add banner to database (activate it)
          const { error: dbError } = await supabase
            .from('banners')
            .insert([
              {
                url: publicUrl,
                desktop: bannerType === 'desktop'
              }
            ]);

          if (dbError) {
            console.error('Error activating banner:', dbError);
            errorCount++;
          } else {
            successCount++;
          }
        } catch (fileError) {
          console.error(`Error processing ${file.name}:`, fileError);
          errorCount++;
        }
      }

      // Clear form and refresh banners
      setSelectedFiles([]);
      setPreviewUrls([]);
      fetchBanners();
      fetchExistingBanners();
      
      // Show detailed success/error message
      if (successCount > 0 && errorCount === 0) {
        showToast(`${successCount} banner(s) optimizados y subidos exitosamente!`, 'success');
      } else if (successCount > 0 && errorCount > 0) {
        showToast(`${successCount} banner(s) subidos exitosamente, ${errorCount} fallaron. Revise la consola para más detalles.`, 'warning');
      } else {
        showToast('Error al subir banners. Revise la consola para más detalles.', 'error');
      }
    } catch (error) {
      console.error('Error uploading banners:', error);
      showToast('Error al subir banners. Intente nuevamente.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const deleteBanner = async (id, url) => {
    // Check authentication
    if (!user) {
      showToast('Debes iniciar sesión para eliminar banners', 'error');
      return;
    }
    
    const deleteId = Date.now(); // Unique identifier for this deletion
    console.log(`[${deleteId}] Attempting to delete banner with ID: ${id} and URL: ${url}`);
    console.log(`[${deleteId}] Current banners before delete:`, banners);
    
    try {
      // First, check if the banner exists before deletion
      const { data: beforeData, error: beforeError } = await supabase
        .from('banners')
        .select('*')
        .eq('id', id);
      
      if (beforeError) {
        console.error(`[${deleteId}] Error checking banner before deletion:`, beforeError);
      } else {
        console.log(`[${deleteId}] Banner exists before deletion:`, beforeData);
      }

      // Only remove from database (keep in storage)
      const { data: deleteData, error: deleteError, count } = await supabase
        .from('banners')
        .delete()
        .eq('id', id)
        .select(); // Get the deleted data to confirm what was deleted

      if (deleteError) {
        console.error(`[${deleteId}] Error deleting banner from database:`, deleteError);
        showToast('Error al eliminar banner de la base de datos. Intente nuevamente.', 'error');
        return;
      }

      console.log(`[${deleteId}] Delete operation result:`, { deleteData, count });
      console.log(`[${deleteId}] Rows affected by delete:`, deleteData?.length || 0);

      if (!deleteData || deleteData.length === 0) {
        console.error(`[${deleteId}] DELETE FAILED - No rows were deleted!`);
        showToast('Error: Banner no fue eliminado de la base de datos', 'error');
        return;
      }

      console.log(`[${deleteId}] Banner successfully deleted from database:`, deleteData);
      
      // Verify the deletion worked
      const { data: verifyData, error: verifyError } = await supabase
        .from('banners')
        .select('id')
        .eq('id', id);
      
      if (verifyError) {
        console.error(`[${deleteId}] Error verifying deletion:`, verifyError);
      } else {
        console.log(`[${deleteId}] Verification query result:`, verifyData);
        if (verifyData && verifyData.length > 0) {
          console.error(`[${deleteId}] DELETION FAILED - Banner still exists in database!`);
          showToast('Error: Banner no fue eliminado de la base de datos', 'error');
          return;
        } else {
          console.log(`[${deleteId}] DELETION CONFIRMED - Banner no longer exists in database`);
        }
      }
      
      // Immediately update local state to remove the banner
      setBanners(prevBanners => {
        const newBanners = prevBanners.filter(banner => banner.id !== id);
        console.log(`[${deleteId}] Updated banners state:`, newBanners);
        return newBanners;
      });
      
      showToast('Banner eliminado de activos', 'success');
    } catch (error) {
      console.error(`[${deleteId}] Error deleting banner:`, error);
      showToast('Error al eliminar banner. Intente nuevamente.', 'error');
    }
  };

  const selectExistingBanner = async (banner) => {
    // Check authentication
    if (!user) {
      showToast('Debes iniciar sesión para activar banners', 'error');
      return;
    }
    
    try {
      // Check if banner is already active
      const existingBanner = banners.find(b => b.url === banner.url);
      if (existingBanner) {
        showToast('¡Banner ya está activo!', 'warning');
        return;
      }

      // Add selected banner to database
      const { error } = await supabase
        .from('banners')
        .insert([
          {
            url: banner.url,
            desktop: banner.type === 'desktop'
          }
        ]);

      if (error) {
        console.error('Error adding banner to database:', error);
        showToast('Error al agregar banner. Intente nuevamente.', 'error');
      } else {
        fetchBanners();
        showToast('¡Banner agregado exitosamente!', 'success');
      }
    } catch (error) {
      console.error('Error adding banner:', error);
      showToast('Error al agregar banner. Intente nuevamente.', 'error');
    }
  };

  const testStorageConnection = async () => {
    try {
      console.log('Testing storage connection...');
      
      // Test listing desktop banners
      const { data: desktopData, error: desktopError } = await supabase.storage
        .from('resources')
        .list('banner desktop');
      
      if (desktopError) {
        console.error('Desktop folder error:', desktopError);
      } else {
        console.log('Desktop banners found:', desktopData);
      }
      
      // Test listing mobile banners
      const { data: mobileData, error: mobileError } = await supabase.storage
        .from('resources')
        .list('banner mobile');
      
      if (mobileError) {
        console.error('Mobile folder error:', mobileError);
      } else {
        console.log('Mobile banners found:', mobileData);
      }
      
      showToast('Prueba de conexión completada. Revise la consola para más detalles.', 'info');
    } catch (error) {
      console.error('Storage test error:', error);
      showToast('Prueba de conexión falló. Revise la consola para más detalles.', 'error');
    }
  };

  const deleteFromStorage = async (banner) => {
    // Check authentication
    if (!user) {
      showToast('Debes iniciar sesión para eliminar banners del almacenamiento', 'error');
      return;
    }
    
    if (!window.confirm(`¿Está seguro que desea eliminar permanentemente "${banner.name}" del almacenamiento? Esta acción no se puede deshacer.`)) return;

    try {
      const folderPath = banner.type === 'desktop' ? 'banner desktop' : 'banner mobile';
      const filePath = `${folderPath}/${banner.name}`;
      
      console.log(`Deleting file from storage: ${filePath}`);
      
      const { error: storageError } = await supabase.storage
        .from('resources')
        .remove([filePath]);

      if (storageError) {
        console.error('Error deleting from storage:', storageError);
        showToast('Error al eliminar banner del almacenamiento. Intente nuevamente.', 'error');
        return;
      }

      // Also remove from active banners if it exists there
      const activeBanner = banners.find(b => b.url === banner.url);
      if (activeBanner) {
        console.log(`Removing banner with ID ${activeBanner.id} from active banners`);
        const { error: dbError } = await supabase
          .from('banners')
          .delete()
          .eq('id', activeBanner.id);
        
        if (dbError) {
          console.error('Error removing from active banners:', dbError);
          showToast('Archivo eliminado pero no se pudo quitar de banners activos', 'warning');
        }
      }

      // Refresh both lists
      await fetchBanners();
      await fetchExistingBanners();
      
      showToast('¡Banner eliminado permanentemente del almacenamiento!', 'success');
    } catch (error) {
      console.error('Error deleting banner from storage:', error);
      showToast('Error al eliminar banner. Intente nuevamente.', 'error');
    }
  };

  // Toast notification functions
  const showToast = (message, type = 'info') => {
    const id = Date.now();
    const toast = { id, message, type };
    setToasts(prev => [...prev, toast]);
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const desktopBanners = useMemo(() => {
    const filtered = banners.filter(banner => banner.desktop === true);
    console.log('Desktop banners recalculated:', filtered);
    return filtered;
  }, [banners]);

  const mobileBanners = useMemo(() => {
    const filtered = banners.filter(banner => banner.desktop === false);
    console.log('Mobile banners recalculated:', filtered);
    return filtered;
  }, [banners]);

  const debugBanners = async () => {
    console.log('=== BANNER DEBUG INFO ===');
    console.log('Current banners state:', banners);
    console.log('Desktop banners:', desktopBanners);
    console.log('Mobile banners:', mobileBanners);
    console.log('Banners length:', banners.length);
    console.log('Desktop banners length:', desktopBanners.length);
    console.log('Mobile banners length:', mobileBanners.length);
    
    // Check current user and permissions
    const { data: user, error: userError } = await supabase.auth.getUser();
    if (userError) {
      console.error('Error getting user:', userError);
    } else {
      console.log('Current user:', user);
    }
    
    // Check if we can read from the banners table
    const { data: dbData, error: dbError } = await supabase
      .from('banners')
      .select('*')
      .limit(100);
    
    if (dbError) {
      console.error('Error reading banners from database:', dbError);
    } else {
      console.log('Direct database query result:', dbData);
      console.log('Database shows', dbData.length, 'banners');
    }
    
    console.log('=== END DEBUG INFO ===');
    
    // Force a re-fetch to ensure consistency (with delay)
    setTimeout(() => {
      fetchBanners();
    }, 100);
  };

  if (loading || authLoading) {
    return <div className="upload-banners-loading">Cargando banners...</div>;
  }

  // If user is not authenticated, show login message
  if (!user) {
    return (
      <>
        <AdminNavBar user={user} />
        <div className="upload-banners-container">
          <div className="upload-banners-header">
            <h1>Gestión de Banners</h1>
            <div className="header-actions">
              
            </div>
          </div>
          <div className="auth-message">
            <h2>Acceso Restringido</h2>
            <p>Debes iniciar sesión para acceder a las funciones administrativas de banners.</p>
            
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavBar user={user} />
      <div className="upload-banners-container">
        <div className="upload-banners-header">
          <h1>Gestión de Banners</h1>
          <div className="header-actions">
            {user && (
              <>
                <span className="user-info">
                  Conectado como: {user.email}
                </span>
              
              </>
            )}
            
          </div>
        </div>

      {/* Upload Section */}
      <div className="upload-section">
        <h2>Subir y Activar Banners</h2>
        <p className="upload-description">
          Sube banners y se activarán automáticamente en tu sitio web.
        </p>
        
        <div className="banner-type-selector">
          <label>
            <input 
              type="radio" 
              value="desktop" 
              checked={bannerType === 'desktop'} 
              onChange={(e) => setBannerType(e.target.value)}
            />
            Banners de Escritorio (1920x600px recomendado)
          </label>
          <label>
            <input 
              type="radio" 
              value="mobile" 
              checked={bannerType === 'mobile'} 
              onChange={(e) => setBannerType(e.target.value)}
            />
            Banners Móviles (640x800px recomendado)
          </label>
        </div>

        <div className="file-input-container">
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="file-input"
            id="banner-upload"
          />
          <label htmlFor="banner-upload" className="file-input-label">
            Seleccionar Imágenes
          </label>
        </div>

        {previewUrls.length > 0 && (
          <div className="preview-section">
            <h3>Vista Previa:</h3>
            <div className="preview-grid">
              {previewUrls.map((url, index) => (
                <div key={index} className="preview-item">
                  <img 
                    src={url} 
                    alt={`Vista previa ${index + 1}`}
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '300px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                  <p>{selectedFiles[index]?.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <button 
          onClick={uploadBanners}
          disabled={uploading || selectedFiles.length === 0}
          className="upload-button"
        >
          {uploading ? 'Subiendo...' : 'Subir'}
        </button>
      </div>

      {/* Choose from Existing Section */}
      <div className="existing-banners-section">
        <h2>Banners Disponibles</h2>
        <button 
          onClick={() => {
            setShowExistingBanners(!showExistingBanners);
            if (!showExistingBanners) {
              fetchExistingBanners();
            }
          }}
          className="toggle-existing-button"
        >
          {showExistingBanners ? 'Ocultar' : 'Mostrar Banners Disponibles'}
        </button>

        {showExistingBanners && (
          <div className="existing-banners-content">
            {loadingExisting ? (
              <div className="loading-message">Cargando banners disponibles...</div>
            ) : (
              <div className="existing-banners-grid">
                {/* Desktop Banners */}
                {existingBanners.desktop.length > 0 && (
                  <div className="existing-section">
                    <h3>Banners de Escritorio ({existingBanners.desktop.length})</h3>
                    <div className="existing-grid">
                      {existingBanners.desktop.map((banner, index) => (
                        <div key={index} className="existing-banner-card">
                          <div className="existing-banner-image">
                            <img src={banner.url} alt={banner.name} />
                          </div>
                          <div className="existing-banner-info">
                            <p className="banner-name">{banner.name}</p>
                            <button 
                              onClick={() => selectExistingBanner(banner)}
                              className="select-banner-button"
                            >
                              Activar
                            </button>
                            <button 
                              onClick={() => deleteFromStorage(banner)}
                              className="delete-button"
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mobile Banners */}
                {existingBanners.mobile.length > 0 && (
                  <div className="existing-section">
                    <h3>Banners Móviles ({existingBanners.mobile.length})</h3>
                    <div className="existing-grid">
                      {existingBanners.mobile.map((banner, index) => (
                        <div key={index} className="existing-banner-card">
                          <div className="existing-banner-image mobile">
                            <img src={banner.url} alt={banner.name} />
                          </div>
                          <div className="existing-banner-info">
                            <p className="banner-name">{banner.name}</p>
                            <button 
                              onClick={() => selectExistingBanner(banner)}
                              className="select-banner-button"
                            >
                              Activar
                            </button>
                            <button 
                              onClick={() => deleteFromStorage(banner)}
                              className="delete-button"
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {existingBanners.desktop.length === 0 && existingBanners.mobile.length === 0 && (
                  <div className="no-banners-message">
                    No hay banners disponibles. ¡Sube algunos banners primero!
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Active Banners Section */}
      <div className="banners-section">
        <h2>Banners Activos</h2>
        
        {/* Desktop Banners */}
        <div className="banner-subsection">
          <h3>Banners de Escritorio ({desktopBanners.length})</h3>
          <div className="banners-grid">
            {desktopBanners.map((banner) => (
              <div key={`desktop-${banner.id}-${banner.url}`} className="banner-card">
                <div className="banner-image-container">
                  <img src={banner.url} alt="Banner de Escritorio" />
                </div>
                <div className="banner-actions">
                  <button 
                    onClick={() => deleteBanner(banner.id, banner.url)}
                    className="delete-button"
                    title="Desactivar banner"
                  >
                    Desactivar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Banners */}
        <div className="banner-subsection">
          <h3>Banners Móviles ({mobileBanners.length})</h3>
          <div className="banners-grid">
            {mobileBanners.map((banner) => (
              <div key={`mobile-${banner.id}-${banner.url}`} className="banner-card">
                <div className="banner-image-container mobile">
                  <img src={banner.url} alt="Banner Móvil" />
                </div>
                <div className="banner-actions">
                  <button 
                    onClick={() => deleteBanner(banner.id, banner.url)}
                    className="delete-button"
                    title="Desactivar banner"
                  >
                    Desactivar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <div className="toast-container">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            <span>{toast.message}</span>
            <button 
              className="toast-close"
              onClick={() => removeToast(toast.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
    </>
  );
}

export default UploadBanners;
